/**
 * F-145 US2 (FR-005/006, SC-002): the warn-and-confirm body shown inside the
 * house `Dialog` before excluding a subsection that holds approved (designer-
 * `included`) cases. It names the affected cases — COUNT and IDENTITIES (Test ID
 * + display name) — so the designer can see exactly what approval the cascade
 * would discard, then choose to proceed or change nothing. Purely presentational:
 * the parent owns the cascade (`onConfirm`) and the dismissal (`onCancel`); the
 * Dialog owns focus-trap + Esc, so this body only contributes real, focusable
 * buttons (keyboard-operable, SC-008). PHI-free — Test IDs / names are
 * template-derived structural identifiers (§IX).
 */
export type ApprovedCaseSummary = {
  readonly id: string;
  /** The Test ID handle (accented secondary identity). */
  readonly testId: string;
  /** The case's display name (canonical name → Function → Test ID fallback). */
  readonly name: string;
};

export type ExcludeSubsectionConfirmProps = {
  readonly subsectionTitle: string;
  readonly approvedCases: readonly ApprovedCaseSummary[];
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
};

export function ExcludeSubsectionConfirm({
  subsectionTitle,
  approvedCases,
  onCancel,
  onConfirm,
}: ExcludeSubsectionConfirmProps) {
  const count = approvedCases.length;
  const plural = count === 1 ? '' : 's';
  return (
    <div className="flex flex-col gap-3">
      {/* The count sentence is a single text node (no nested element interrupts
          "{N} approved test case(s)") so assistive tech + the SC-002 assertion read
          the count + the consequence in one phrase. */}
      <p className="text-md leading-snug text-text-primary">
        {count} approved test case{plural} {count === 1 ? 'is' : 'are'} included in “
        {subsectionTitle}”. Excluding the subsection will exclude {count === 1 ? 'it' : 'them'} from
        the rendered script.
      </p>
      <ul
        data-approved-cases
        className="m-0 flex max-h-48 list-none flex-col gap-1 overflow-auto rounded-md border border-border bg-ink-50 p-2"
      >
        {approvedCases.map((approvedCase) => (
          <li key={approvedCase.id} className="flex items-baseline gap-2 px-1 py-0.5">
            <span className="font-mono text-sm font-semibold text-[#4c70f0]">
              {approvedCase.testId}
            </span>
            <span className="min-w-0 truncate text-md text-text-primary">{approvedCase.name}</span>
          </li>
        ))}
      </ul>
      <div className="mt-1 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-9 cursor-pointer items-center rounded-md border border-border bg-surface-card px-4 text-md font-medium text-text-primary hover:bg-ink-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="inline-flex h-9 cursor-pointer items-center rounded-md border-0 bg-[#d94433] px-4 text-md font-medium text-white hover:brightness-110"
        >
          Exclude anyway
        </button>
      </div>
    </div>
  );
}
